import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  PhotoIcon,
  StarIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { useGalleries } from '../../contexts/GalleryContext';
import { uploadImageToStorage } from '../../utils/imageUpload';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop&q=80';

interface GalleryFormData {
  title: string;
  description: string;
  is_featured: boolean;
}

const GalleryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addGallery, updateGallery, getGallery } = useGalleries();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<GalleryFormData>({
    title: '',
    description: '',
    is_featured: false
  });

  const [images, setImages] = useState<string[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof GalleryFormData, string>>>({});
  const [uploading, setUploading] = useState(false);

  // Edit mode: Load existing gallery data
  useEffect(() => {
    if (isEditMode && id) {
      const gallery = getGallery(Number(id));
      if (gallery) {
        setFormData({
          title: gallery.title,
          description: gallery.description || '',
          is_featured: gallery.is_featured
        });
        // images 배열이 있으면 사용, 없으면 기존 image_url로 폴백
        if (gallery.images && gallery.images.length > 0) {
          setImages(gallery.images);
        } else if (gallery.image_url && gallery.image_url !== PLACEHOLDER_IMAGE) {
          setImages([gallery.image_url]);
        }
      }
    }
  }, [isEditMode, id, getGallery]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name as keyof GalleryFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // 파일 검증
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드할 수 있습니다.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`파일 "${file.name}"의 크기가 10MB를 초과합니다.`);
        return;
      }
    }

    try {
      setUploading(true);

      const uploadedUrls: string[] = [];
      for (const file of fileArray) {
        const imageUrl = await uploadImageToStorage(file, 'gallery-images');
        uploadedUrls.push(imageUrl);
      }

      setImages(prev => [...prev, ...uploadedUrls]);
    } catch (error) {
      console.error('Failed to upload gallery images:', error);
      alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUploading(false);
      // input 초기화 (같은 파일 재선택 가능하도록)
      e.target.value = '';
    }
  };

  const handleImageRemove = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageMoveUp = (index: number) => {
    if (index === 0) return;
    setImages(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const handleImageMoveDown = (index: number) => {
    if (index === images.length - 1) return;
    setImages(prev => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof GalleryFormData, string>> = {};
    if (!formData.title.trim()) newErrors.title = '제목은 필수입니다.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const representativeImage = images.length > 0 ? images[0] : PLACEHOLDER_IMAGE;

        const galleryData = {
          title: formData.title,
          description: formData.description,
          is_featured: formData.is_featured,
          image_url: representativeImage,
          thumbnail_url: representativeImage,
          images: images
        };

        if (isEditMode && id) {
          await updateGallery(Number(id), galleryData);
          alert('갤러리가 성공적으로 수정되었습니다!');
        } else {
          await addGallery(galleryData);
          alert('갤러리가 성공적으로 등록되었습니다!');
        }
        navigate('/admin/gallery');
      } catch (error) {
        console.error('Failed to submit gallery:', error);
        alert('갤러리 저장에 실패했습니다.');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isEditMode ? '갤러리 수정' : '갤러리 등록'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? '갤러리 내용을 수정합니다.' : '새로운 갤러리를 등록합니다.'}
          <span className="text-red-500">*</span> 표시는 필수 항목입니다.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
        {/* 이미지 업로드 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">이미지 업로드</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이미지 <span className="text-gray-500 text-xs">(여러 장 선택 가능 · 첫 번째 이미지가 대표 이미지)</span>
            </label>

            {/* 이미지 미리보기 목록 */}
            {images.length > 0 && (
              <div className="mb-4 space-y-3">
                {images.map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
                      index === 0 ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <img
                      src={url}
                      alt={`이미지 ${index + 1}`}
                      className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {index === 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            대표
                          </span>
                        )}
                        <span className="text-sm text-gray-600">
                          {index + 1} / {images.length}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleImageMoveUp(index)}
                        disabled={index === 0}
                        className={`p-1.5 rounded ${
                          index === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-200'
                        }`}
                        title="위로 이동"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImageMoveDown(index)}
                        disabled={index === images.length - 1}
                        className={`p-1.5 rounded ${
                          index === images.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-200'
                        }`}
                        title="아래로 이동"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        title="이미지 삭제"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 업로드 버튼 */}
            <div className="flex items-center gap-4">
              <label className={`flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                uploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}>
                <PhotoIcon className="h-5 w-5 mr-2" />
                {uploading ? '업로드 중...' : '이미지 선택'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-500">
                JPG, PNG, GIF 등 (파일당 최대 10MB)
              </span>
            </div>
          </div>
        </div>

        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>

          <div className="space-y-6">
            {/* 제목 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="갤러리 제목을 입력하세요"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* 설명 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                설명
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="갤러리에 대한 설명을 입력하세요 (선택사항)"
              />
            </div>

            {/* 메인 노출 */}
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      is_featured: e.target.checked
                    });
                  }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <StarIcon className="h-5 w-5 text-yellow-500" />
                <span className="text-sm font-medium text-gray-700">메인에 노출</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                체크하면 메인 페이지에 이 갤러리가 표시됩니다.
              </p>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/gallery')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={uploading}
            className={`px-6 py-2 rounded-lg text-white ${
              uploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isEditMode ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GalleryForm;
